'use client'

import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { sanitizeHref } from '@/lib/sanitize'
import { Button } from '@/components/ui/button'
import {
  X,
  Mail,
  KeyRound,
  CheckCircle2,
  Loader2,
  Sparkles,
  ArrowRight,
} from 'lucide-react'
import { radarAnalytics } from '@/lib/analytics/radar-events'
import { getAttributionEnvelope } from '@/lib/analytics/first-touch-utm'
import { peekVisitorId } from '@/lib/analytics/visitor-id'
import type { FreeToolSlug } from '@/lib/tool-access'
import { RETAINER_AUDITS } from '@/lib/paddle/pricing'

/**
 * useToolAccessGate — hook + JSX modal for the /tools/* free gate.
 *
 * Lazy: the modal does not render on mount. The tool page calls openGate()
 * from its submit handler when !ready. After a successful audit run, the
 * page must call markConsumed() so the next click re-opens the modal.
 *
 *   const gate = useToolAccessGate('crawl-check', 'AI Crawl Checker')
 *
 *   function handleSubmit(url: string) {
 *     if (!gate.ready) return gate.openGate(url, () => void run(url))
 *     void run(url)
 *   }
 *
 *   async function run(url: string) {
 *     // ... fetch, never re-checks gate.ready ...
 *     gate.markConsumed()
 *   }
 *
 *   return <>{gate.modal}{ ...existing JSX... }</>
 *
 * The resume callback (PIX-47) is how the typed domain survives the gate:
 * after the code verifies, the hook calls it once, so the check the visitor
 * asked for runs without a second click. It must call the fetch path, not the
 * submit handler: that handler's closure still sees `gate.ready === false`
 * and would reopen the popup.
 */

/** How long the "Send a new code" button stays disabled after each send. */
const RESEND_COOLDOWN_MS = 30_000

/** What GET /api/tools/access/me answers. */
interface AccessState {
  isPaid?: boolean
  hasCredit?: boolean
  verifiedEmail?: unknown
}

/**
 * Ask the server what this visitor may do. `null` means the plan lookup
 * itself failed (a 503, PIX-130): unknown, never "not paid", because treating
 * it as not paid opens the free email step for a paying customer.
 */
async function fetchAccessState(
  toolSlug: FreeToolSlug
): Promise<AccessState | null> {
  const res = await fetch(
    `/api/tools/access/me?tool=${encodeURIComponent(toolSlug)}`,
    { cache: 'no-store' }
  )
  if (res.status === 503) return null
  return res.json().catch(() => ({}))
}

/** The one reading of an access answer, shared by page load and retry. */
function accessOutcome(
  data: AccessState
):
  | { kind: 'paid' }
  | { kind: 'credit' }
  | { kind: 'used'; email: string }
  | { kind: 'new' } {
  if (data.isPaid) return { kind: 'paid' }
  if (data.hasCredit) return { kind: 'credit' }
  if (data.verifiedEmail && typeof data.verifiedEmail === 'string') {
    return { kind: 'used', email: data.verifiedEmail }
  }
  return { kind: 'new' }
}

type Status =
  | 'idle'
  | 'email'
  | 'sending'
  | 'code'
  | 'verifying'
  | 'ready'
  | 'capped'
  | 'checking'
  // Returning email that already spent its one free run. No form — just the
  // upsell. This is what closes the re-verify farming loop at the UI.
  | 'used'
  // The plan lookup failed (PIX-130): "couldn't confirm, try again", never
  // the free email step a paying customer would otherwise be sent to.
  | 'unavailable'

/** Payload of the 429 domain_cap response from /api/tools/* routes. */
export interface DomainCapInfo {
  domain: string
  message: string
  cachedResultUrl: string | null
  upsellUrl: string
}

interface UpsellPayload {
  trigger: 'repeat_audits'
  priorRequests: number
  cta: 'pro_retainer'
  ctaUrl: string
  headline: string
  body: string
}

export interface ToolGate {
  ready: boolean
  email: string | null
  isPaid: boolean
  openGate: (target?: string, resume?: () => void) => void
  markConsumed: () => void
  showDomainCap: (info: DomainCapInfo) => void
  modal: ReactNode
}

export function useToolAccessGate(
  toolSlug: FreeToolSlug,
  toolName: string
): ToolGate {
  const [open, setOpen] = useState(false)
  const [status, setStatus] = useState<Status>('idle')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState('')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [isPaid, setIsPaid] = useState(false)
  const [upsell, setUpsell] = useState<UpsellPayload | null>(null)
  const [upsellDismissed, setUpsellDismissed] = useState(false)
  const [capInfo, setCapInfo] = useState<DomainCapInfo | null>(null)
  // Server ran the paid-user sign-in recovery for this email (uniform flag —
  // an email went out only if the account actually qualifies).
  const [recoveryInitiated, setRecoveryInitiated] = useState(false)
  // The page-load plan lookup failed (PIX-130). Analyze then shows the
  // 'unavailable' step with a retry instead of the email step.
  // A ref, not state: openGate's async cap check and the load answer race,
  // and a callback must read the latest value, never the one it captured.
  const planUnknownRef = useRef(false)
  // The unticked product-news box (PIX-47). Product news is its own choice,
  // separate from the code and the audit follow-ups.
  const [productNews, setProductNews] = useState(false)
  // An address the SERVER has confirmed spent its free run (/me, an
  // already_used answer, or a run this page just consumed). Only this shows
  // the upsell: the typed `email` is just input, and treating it as "used"
  // trapped anyone who typed an address and closed the popup.
  const [usedEmail, setUsedEmail] = useState<string | null>(null)
  // The address a code was sent to, with the product-news choice frozen at
  // that moment. A resend reuses both, so it can never flip the choice.
  const [codeSent, setCodeSent] = useState<{
    email: string
    productNews: boolean
  } | null>(null)
  const [resending, setResending] = useState(false)
  const [resendCooling, setResendCooling] = useState(false)
  const [resent, setResent] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const cooldownTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Resume-after-grant (see the header). The generation number makes a
  // callback stale the moment the popup closes or reopens, so a later grant
  // can never run a check the visitor walked away from.
  const resumeRef = useRef<{ gen: number; fn: () => void } | null>(null)
  const genRef = useRef(0)

  useEffect(
    () => () => {
      if (cooldownTimer.current) clearTimeout(cooldownTimer.current)
    },
    []
  )

  // Runs the pending check once the grant has flipped `ready`. The ref is
  // cleared BEFORE the call, so a re-render or Strict Mode's double effect
  // cannot run it twice.
  useEffect(() => {
    if (!ready) return
    const pending = resumeRef.current
    resumeRef.current = null
    if (pending && pending.gen === genRef.current) pending.fn()
  }, [ready])

  const startCooldown = useCallback(() => {
    if (cooldownTimer.current) clearTimeout(cooldownTimer.current)
    setResendCooling(true)
    cooldownTimer.current = setTimeout(
      () => setResendCooling(false),
      RESEND_COOLDOWN_MS
    )
  }, [])

  useEffect(() => {
    let cancelled = false
    async function init() {
      try {
        const data = await fetchAccessState(toolSlug)
        if (cancelled) return
        if (data === null) {
          // Unknown plan: no popup on load. Analyze explains and retries.
          planUnknownRef.current = true
          // Analyze may already have opened the gate before this answer
          // arrived: move it off the free email step (PIX-130).
          setStatus(s =>
            s === 'email' || s === 'checking' ? 'unavailable' : s
          )
          return
        }
        const outcome = accessOutcome(data)
        if (outcome.kind === 'paid') {
          setIsPaid(true)
          setReady(true)
          return
        }
        if (outcome.kind === 'credit') {
          setReady(true)
          return
        }
        if (outcome.kind === 'used') {
          // Already verified once + no active credit = free run spent.
          // Show the upsell, not another verify form (that loop is the abuse).
          setEmail(outcome.email)
          setUsedEmail(outcome.email)
          setStatus('used')
          setOpen(true)
          radarAnalytics.toolAccessGateViewed(toolSlug)
          return
        }
        // New visitor — open the email lead-capture form for their one free run.
        setStatus('email')
        setOpen(true)
        radarAnalytics.toolAccessGateViewed(toolSlug)
      } catch {
        // Non-fatal — gate just opens on submit.
      }
    }
    void init()
    return () => {
      cancelled = true
    }
  }, [toolSlug])

  // When the page passes the typed URL/brand, pre-check the domain cap so
  // an already-capped domain jumps straight to the upsell dialog instead
  // of collecting an email verification that would be blocked at run time.
  // Advisory only (fails open to the email step) — the tool route enforces.
  const openGate = useCallback(
    (target?: string, resume?: () => void) => {
      if (ready) return
      genRef.current += 1
      resumeRef.current = resume ? { gen: genRef.current, fn: resume } : null
      setOpen(true)
      radarAnalytics.toolAccessGateViewed(toolSlug)
      setErrorMsg(null)
      setNotice(null)
      // The plan lookup failed on load: say so and offer a retry, never the
      // free email step (PIX-130).
      if (planUnknownRef.current) {
        setStatus('unavailable')
        return
      }
      // Server-confirmed used address → upsell, no form, no checks.
      if (usedEmail) {
        setStatus('used')
        return
      }
      // A code already went to the address in the box: go back to it (with
      // resend) rather than making the visitor start over.
      if (codeSent && codeSent.email === email.trim().toLowerCase()) {
        setStatus('code')
        return
      }
      if (!target) {
        setStatus('email')
        return
      }
      setStatus('checking')
      // The same generation that guards the resume: a check that resolves
      // after the popup closed or reopened must not overwrite the newer step.
      const gen = genRef.current
      void (async () => {
        let capped: DomainCapInfo | null = null
        try {
          const res = await fetch(
            `/api/tools/access/cap-check?tool=${encodeURIComponent(toolSlug)}&target=${encodeURIComponent(target)}`,
            { cache: 'no-store' }
          )
          const data = await res.json().catch(() => ({}))
          if (data?.capped) capped = data as DomainCapInfo
        } catch {
          // Fail open — run-time check still enforces.
        }
        if (gen !== genRef.current) return
        // The load answer may have failed while this check was in flight.
        if (planUnknownRef.current) {
          setStatus('unavailable')
          return
        }
        if (capped) {
          setCapInfo(capped)
          setStatus('capped')
          return
        }
        setStatus('email')
      })()
    },
    [ready, email, usedEmail, codeSent, toolSlug]
  )

  /**
   * Retry after a failed plan lookup. A payer is granted and the check they
   * asked for resumes (the resume effect above); anyone else continues to the
   * step their answer calls for.
   */
  const retryPlan = useCallback(async () => {
    setStatus('checking')
    let data: AccessState | null
    try {
      data = await fetchAccessState(toolSlug)
    } catch (error) {
      // Transport failure on the retry: same "couldn't confirm" state, but
      // the cause stays visible for diagnosis.
      console.warn('[ToolAccessGate] plan re-check failed:', error)
      data = null
    }
    if (data === null) {
      setStatus('unavailable')
      return
    }
    planUnknownRef.current = false
    const outcome = accessOutcome(data)
    if (outcome.kind === 'paid' || outcome.kind === 'credit') {
      if (outcome.kind === 'paid') setIsPaid(true)
      setOpen(false)
      setStatus('idle')
      setReady(true)
      return
    }
    if (outcome.kind === 'used') {
      setEmail(outcome.email)
      setUsedEmail(outcome.email)
      setStatus('used')
      return
    }
    setStatus('email')
  }, [toolSlug])

  const markConsumed = useCallback(() => {
    if (isPaid) return
    setReady(false)
    setCode('')
    setStatus('idle')
    setCodeSent(null)
    // The run that just finished spent this address's free audit.
    const spent = email.trim().toLowerCase()
    if (spent) setUsedEmail(spent)
    radarAnalytics.toolAccessGateRunConsumed(toolSlug)
  }, [isPaid, email, toolSlug])

  const close = useCallback(() => {
    if (status === 'sending' || status === 'verifying') return
    setOpen(false)
    setErrorMsg(null)
    // Walking away cancels the pending check (see resumeRef).
    genRef.current += 1
    resumeRef.current = null
  }, [status])

  // Domain hit its free-run cap (429 domain_cap from the tool route).
  // Shows the cap explanation + Radar upsell INSTEAD of the email/OTP
  // form — re-verifying with another email cannot unlock a capped domain.
  const showDomainCap = useCallback(
    (info: DomainCapInfo) => {
      setCapInfo(info)
      setStatus('capped')
      setOpen(true)
      radarAnalytics.toolAccessGateViewed(toolSlug)
    },
    [toolSlug]
  )

  /**
   * One code request, first or resend. A first send takes the address and
   * box from the form; a resend reuses `codeSent`, so the choice recorded on
   * the new code row is the one the visitor made, not whatever the box shows.
   * A failed resend keeps the visitor on the code step.
   */
  const sendCode = useCallback(
    async (
      target: { email: string; productNews: boolean },
      resend: boolean
    ) => {
      if (resend) setResending(true)
      else setStatus('sending')
      setErrorMsg(null)
      setNotice(null)
      // A failure returns to the step it came from, with the message in
      // that step's form: the email form for a first send, the code form
      // for a resend.
      const failed = (message: string) => {
        setErrorMsg(message)
        setStatus(resend ? 'code' : 'email')
      }
      try {
        const res = await fetch('/api/tools/access/request-code', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          // Carries the consent-gated first-touch record AND why it is empty
          // when it is empty. Without this the nine tool routes had no channel
          // to write, which is why every lead in the 2026-09-19 audit window
          // had blank utm_* columns.
          body: JSON.stringify({
            email: target.email,
            tool: toolSlug,
            productNews: target.productNews,
            ...getAttributionEnvelope(),
          }),
        })
        const data = await res.json().catch(() => ({}))
        // Email already used its one free test — go straight to the upsell,
        // no OTP, no error styling.
        if (data?.reason === 'already_used') {
          setUsedEmail(target.email)
          setRecoveryInitiated(Boolean(data?.recoveryInitiated))
          setStatus('used')
          return
        }
        if (!res.ok) {
          failed(friendlyError(data?.reason))
          return
        }
        if (data?.bypass) {
          setReady(true)
          setOpen(false)
          setStatus('idle')
          radarAnalytics.toolAccessGateGranted(toolSlug, 'bypass')
          return
        }
        // Pro Retainer upsell may be attached when the email has hit the
        // repeat-audit threshold. Surfaced as a dismissable card above the
        // OTP form. Never blocks the OTP flow.
        if (data?.upsell) {
          setUpsell(data.upsell as UpsellPayload)
          setUpsellDismissed(false)
        }
        setCodeSent(target)
        startCooldown()
        setStatus('code')
        if (resend) {
          // Only the newest code verifies (verifyOtp reads the newest row).
          setCode('')
          setResent(true)
          setNotice(
            'New code sent. Use the newest one; earlier codes stop working.'
          )
        } else {
          setResent(false)
          radarAnalytics.toolAccessGateCodeRequested(toolSlug)
        }
      } catch {
        failed('Network error. Try again.')
      } finally {
        if (resend) setResending(false)
      }
    },
    [toolSlug, startCooldown]
  )

  const requestCode = useCallback(() => {
    const address = email.trim().toLowerCase()
    if (!address) return
    void sendCode({ email: address, productNews }, false)
  }, [email, productNews, sendCode])

  const resendCode = useCallback(() => {
    if (!codeSent || resending || resendCooling) return
    void sendCode(codeSent, true)
  }, [codeSent, resending, resendCooling, sendCode])

  const switchEmail = useCallback(() => {
    setCode('')
    setErrorMsg(null)
    setNotice(null)
    setStatus('email')
  }, [])

  const submitCode = useCallback(async () => {
    if (!/^\d{6}$/.test(code.trim())) {
      setErrorMsg('Enter the 6-digit code from the email.')
      return
    }
    setStatus('verifying')
    setErrorMsg(null)
    setNotice(null)
    try {
      const res = await fetch('/api/tools/access/verify-code', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // Sent again here because this is the request that actually mints the
        // grant. Binding attribution to the credit at creation is what keeps
        // it immune to resends and parallel tabs for the same address.
        body: JSON.stringify({
          // The address the code was SENT to, not whatever the email box
          // holds now: the two can only disagree through a stale step.
          email: codeSent?.email ?? email.trim(),
          code: code.trim(),
          tool: toolSlug,
          ...getAttributionEnvelope(),
          // Customer-journey key (PIX-45): ties this browser to the address
          // it verifies, in our database only. Null without analytics consent.
          visitorId: peekVisitorId(),
        }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.ok) {
        // After a resend an older code fails as "incorrect"; say why.
        setErrorMsg(
          resent && data?.reason === 'invalid'
            ? 'That code is incorrect. Use the newest code we sent; earlier codes stop working.'
            : friendlyError(data?.reason)
        )
        setStatus('code')
        return
      }
      setReady(true)
      setOpen(false)
      setStatus('idle')
      radarAnalytics.toolAccessGateGranted(toolSlug, 'otp')
    } catch {
      setErrorMsg('Network error. Try again.')
      setStatus('code')
    }
  }, [code, codeSent, email, resent, toolSlug])

  const modal = useMemo(() => {
    if (!open) return null
    return (
      <div
        className='fixed inset-0 z-50 flex items-center justify-center p-4'
        style={{ background: 'rgba(0,0,0,0.7)' }}
        onClick={close}
      >
        <div
          className='w-full max-w-md rounded-2xl border p-8 bg-card border-border'
          onClick={e => e.stopPropagation()}
        >
          <div className='flex items-start justify-between mb-6'>
            <div>
              <h3 className='text-xl font-bold text-foreground mb-1'>
                {status === 'unavailable'
                  ? "We couldn't confirm your plan"
                  : status === 'capped'
                    ? 'Free audits used for this domain'
                    : status === 'used'
                      ? "You've used your free audit"
                      : status === 'code' || status === 'verifying'
                        ? 'Enter your verification code'
                        : `Run ${toolName}`}
              </h3>
              <p className='text-sm text-muted-foreground'>
                {status === 'unavailable'
                  ? 'Something went wrong on our side while checking your plan. Try again in a minute.'
                  : status === 'capped'
                    ? `${capInfo?.domain ?? 'This domain'} has reached its free audit limit for the last 30 days.`
                    : status === 'used'
                      ? `Upgrade to Pro for ${RETAINER_AUDITS} full audits a month across every tool.`
                      : status === 'code' || status === 'verifying'
                        ? `We sent a 6-digit code to ${codeSent?.email ?? email}. It expires in 15 minutes.`
                        : // The steps and the reason, before anything is typed
                          // (PIX-47: 158 of 213 left this screen unexplained).
                          "Enter your email and the 6-digit code we send you, and your result appears on this page. Each email gets one free audit, so the code confirms it's yours."}
              </p>
            </div>
            <button
              type='button'
              onClick={close}
              className='p-1 rounded hover:bg-muted transition-colors'
              aria-label='Close'
            >
              <X size={18} className='text-muted-foreground' />
            </button>
          </div>

          {status === 'checking' && (
            <div
              className='flex items-center justify-center py-10'
              role='status'
              aria-label='Checking availability'
            >
              <Loader2
                size={20}
                className='animate-spin text-muted-foreground'
              />
            </div>
          )}

          {status === 'unavailable' && (
            <Button
              type='button'
              variant='neutral'
              size='sm'
              className='w-full'
              onClick={() => void retryPlan()}
            >
              Try again
            </Button>
          )}

          {status === 'used' && (
            <div className='space-y-3'>
              {recoveryInitiated && (
                <p className='text-sm text-foreground/80'>
                  If this email has Radar credits, purchases, or an active
                  subscription, we&apos;ll send a sign-in link. Please check
                  your inbox and spam folder.
                </p>
              )}
              <a
                href='/platform#pricing'
                target='_blank'
                rel='noopener noreferrer'
                className='w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110'
                style={{
                  background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
                  color: '#fff',
                }}
              >
                Need more audits? Upgrade to Pro
                <ArrowRight size={14} />
              </a>
              <p className='text-xs text-muted-foreground text-center'>
                Already purchased?{' '}
                <a
                  href='/platform/sign-in'
                  className='font-medium hover:underline'
                  style={{ color: '#008dcc' }}
                >
                  Sign in
                </a>
              </p>
            </div>
          )}

          {status === 'capped' && capInfo && (
            <div className='space-y-3'>
              <div
                className='rounded-xl border p-4'
                style={{
                  borderColor: 'rgba(139, 92, 246, 0.3)',
                  background:
                    'linear-gradient(135deg, rgba(0, 141, 204, 0.06), rgba(139, 92, 246, 0.06))',
                }}
              >
                <div className='flex items-start gap-2 mb-2'>
                  <Sparkles
                    size={14}
                    className='mt-0.5 flex-shrink-0'
                    style={{ color: '#8b5cf6' }}
                  />
                  <p className='text-sm font-semibold text-foreground'>
                    Keep auditing with Pixelmojo Radar
                  </p>
                </div>
                <p className='text-[12px] text-muted-foreground leading-relaxed'>
                  Free tools give every domain a one-time snapshot. Radar gives
                  you repeat audits, all tools, and fix tracking over time so
                  you can verify every change actually moved your score.
                </p>
              </div>
              <a
                href={sanitizeHref(capInfo.upsellUrl) ?? '/platform#pricing'}
                className='w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110'
                style={{
                  background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
                  color: '#fff',
                }}
              >
                See Radar plans
                <ArrowRight size={14} />
              </a>
              {capInfo.cachedResultUrl && (
                <a
                  href={sanitizeHref(capInfo.cachedResultUrl) ?? '/platform'}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='block text-center text-[12px] font-semibold transition-colors'
                  style={{ color: '#008dcc' }}
                >
                  View the existing report for {capInfo.domain}
                </a>
              )}
            </div>
          )}

          {(status === 'email' || status === 'sending') && (
            <form
              onSubmit={e => {
                e.preventDefault()
                requestCode()
              }}
              className='space-y-3'
            >
              <div className='relative'>
                <Mail
                  size={14}
                  className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                />
                <input
                  type='email'
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value)
                    if (errorMsg) setErrorMsg(null)
                  }}
                  placeholder='your@company.com'
                  required
                  autoFocus
                  disabled={status === 'sending'}
                  className='w-full h-11 pl-9 pr-3 rounded-lg text-sm outline-none transition-colors focus:ring-1'
                  style={{
                    background: 'var(--background)',
                    border: '1px solid var(--control-border)',
                    color: 'var(--foreground)',
                  }}
                />
              </div>
              {/* Product news is its own choice, unticked by default
                  (PIX-47). Stored on the code row at request-code. */}
              <label className='flex items-start gap-2 text-[12px] text-muted-foreground cursor-pointer'>
                <input
                  type='checkbox'
                  checked={productNews}
                  onChange={e => setProductNews(e.target.checked)}
                  disabled={status === 'sending'}
                  className='mt-0.5 h-3.5 w-3.5 flex-shrink-0 accent-primary'
                />
                <span>Also send me occasional Radar product news.</span>
              </label>
              <button
                type='submit'
                disabled={status === 'sending' || !email.trim()}
                className='w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-40'
                style={{
                  background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
                  color: '#fff',
                }}
              >
                {status === 'sending' ? (
                  <>
                    <Loader2 size={14} className='animate-spin' /> Sending…
                  </>
                ) : (
                  'Send my code'
                )}
              </button>
              {errorMsg && (
                <p className='text-[11px]' style={{ color: '#F90B8A' }}>
                  {errorMsg}
                </p>
              )}
              {/* Every use this address is actually put to, and no more.
                  Getting here took two wrong drafts. The original promised to
                  "send results", but a /tools/* result is rendered in-page and
                  never emailed. The replacement then claimed "Nothing else",
                  which was worse: radar_leads feeds the announcement
                  audiences. Product news is now the box above (PIX-47), so
                  this line covers only the code and the audit follow-ups.
                  "May" and "a few" are load-bearing: the llms.txt generator
                  writes no lead and so sends no follow-ups at all, while the
                  lead-producing tools send three. One sentence has to stay
                  true across both. */}
              <p className='text-[11px] text-center text-muted-foreground mt-4'>
                We email your 6-digit code and may send a few short follow-ups
                about this audit. Unsubscribe any time.
              </p>
            </form>
          )}

          {(status === 'code' || status === 'verifying') &&
            upsell &&
            !upsellDismissed && (
              <div
                className='mb-4 rounded-xl border p-4'
                style={{
                  borderColor: 'rgba(139, 92, 246, 0.3)',
                  background:
                    'linear-gradient(135deg, rgba(0, 141, 204, 0.06), rgba(139, 92, 246, 0.06))',
                }}
              >
                <div className='flex items-start gap-2 mb-2'>
                  <Sparkles
                    size={14}
                    className='mt-0.5 flex-shrink-0'
                    style={{ color: '#8b5cf6' }}
                  />
                  <p className='text-sm font-semibold text-foreground'>
                    {upsell.headline}
                  </p>
                </div>
                <p className='text-[12px] text-muted-foreground leading-relaxed mb-3'>
                  {upsell.body}
                </p>
                <div className='flex items-center justify-between gap-2'>
                  <a
                    href={upsell.ctaUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='inline-flex items-center gap-1.5 text-[12px] font-semibold transition-colors'
                    style={{ color: '#008dcc' }}
                  >
                    See Pro Retainer
                    <ArrowRight size={12} />
                  </a>
                  <button
                    type='button'
                    onClick={() => setUpsellDismissed(true)}
                    className='text-[11px] text-muted-foreground hover:text-foreground transition-colors'
                  >
                    Not now
                  </button>
                </div>
              </div>
            )}

          {(status === 'code' || status === 'verifying') && (
            <form
              onSubmit={e => {
                e.preventDefault()
                void submitCode()
              }}
              className='space-y-3'
            >
              <div className='relative'>
                <KeyRound
                  size={14}
                  className='absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground'
                />
                <input
                  type='text'
                  inputMode='numeric'
                  autoComplete='one-time-code'
                  pattern='[0-9]{6}'
                  maxLength={6}
                  value={code}
                  onChange={e => {
                    setCode(e.target.value.replace(/\D/g, ''))
                    if (errorMsg) setErrorMsg(null)
                  }}
                  placeholder='123456'
                  required
                  autoFocus
                  disabled={status === 'verifying'}
                  className='w-full h-11 pl-9 pr-3 rounded-lg text-sm outline-none transition-colors focus:ring-1 tracking-[6px] font-mono'
                  style={{
                    background: 'var(--background)',
                    border: '1px solid var(--control-border)',
                    color: 'var(--foreground)',
                  }}
                />
              </div>
              <button
                type='submit'
                disabled={
                  status === 'verifying' || !/^\d{6}$/.test(code.trim())
                }
                className='w-full h-11 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-40'
                style={{
                  background: 'linear-gradient(135deg, #008dcc, #8b5cf6)',
                  color: '#fff',
                }}
              >
                {status === 'verifying' ? (
                  <>
                    <Loader2 size={14} className='animate-spin' /> Verifying…
                  </>
                ) : (
                  <>
                    <CheckCircle2 size={14} /> Verify and run audit
                  </>
                )}
              </button>
              {errorMsg && (
                <p
                  role='alert'
                  className='text-[11px]'
                  style={{ color: '#F90B8A' }}
                >
                  {errorMsg}
                </p>
              )}
              {notice && !errorMsg && (
                <p role='status' className='text-[11px] text-foreground/80'>
                  {notice}
                </p>
              )}
              {/* Resend (PIX-47): the server already issues a fresh code on a
                  repeat request; the errors above told people to "request a
                  new code" with nothing to click. */}
              <p className='text-[11px] text-muted-foreground'>
                Didn&apos;t get it? Check your spam folder.
              </p>
              <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2 text-[11px]'>
                <div className='flex items-center gap-3'>
                  <Button
                    type='button'
                    variant='link'
                    size='link'
                    onClick={resendCode}
                    disabled={
                      resending || resendCooling || status === 'verifying'
                    }
                    className='text-[11px] font-semibold'
                  >
                    {resending ? 'Sending…' : 'Send a new code'}
                  </Button>
                  {/* Disabled while a resend is in flight, so its reply can
                      never land after the visitor moved to another address. */}
                  <Button
                    type='button'
                    variant='link'
                    size='link'
                    onClick={switchEmail}
                    disabled={resending || status === 'verifying'}
                    className='text-[11px] font-normal text-muted-foreground hover:text-foreground'
                  >
                    Use a different email
                  </Button>
                </div>
                <a
                  href='/platform#pricing'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='font-semibold transition-colors hover:brightness-110'
                  style={{ color: '#8b5cf6' }}
                >
                  Need more audits? Upgrade to Pro →
                </a>
              </div>
            </form>
          )}
        </div>
      </div>
    )
  }, [
    open,
    status,
    toolName,
    email,
    code,
    errorMsg,
    notice,
    upsell,
    upsellDismissed,
    capInfo,
    recoveryInitiated,
    productNews,
    codeSent,
    resending,
    resendCooling,
    close,
    requestCode,
    resendCode,
    switchEmail,
    submitCode,
    retryPlan,
  ])

  return {
    ready,
    email: email || null,
    isPaid,
    openGate,
    markConsumed,
    showDomainCap,
    modal,
  }
}

function friendlyError(reason: unknown): string {
  switch (reason) {
    case 'invalid':
      return 'That code is incorrect. Check your email and try again.'
    case 'expired':
      return 'That code expired. Send a new one below.'
    case 'too_many_attempts':
      return 'Too many wrong attempts. Send a new code below.'
    case 'rate_limit':
      return 'Too many requests. Wait a minute and try again.'
    case 'daily_cap':
      return 'You have hit the daily verification limit for this email.'
    case 'already_used':
      return `This email already used its free run. Upgrade to Pro for ${RETAINER_AUDITS} full audits a month.`
    case 'invalid_email':
      return 'That email looks invalid. Try another.'
    case 'disposable_email':
      return 'Temporary email addresses are not supported. Use your real work or personal email.'
    case 'invalid_tool':
      return 'Unsupported tool. Refresh the page.'
    case 'send_failed':
      return "We couldn't send the code to that address. Check it and try again."
    case 'server_error':
      return 'Our server hit a snag. Try again in a moment.'
    default:
      return 'Something went wrong. Try again.'
  }
}
